/**
 * Freight carrier instructions are extracted as a list of
 * `{ text, appliesTo, condition }` blocks and stored verbatim in the draft's
 * `computedData`. Rendering that list with `String()` shows the user
 * `[object Object]`, so both review pages (v2 schema-driven and v3) go through
 * this normaliser: keep the blocks that carry a real `text`, join them.
 */

/** Loose shape probe — detects an "intended-to-be" freight block. */
export function isFreightInstructionBlockLike(
  value: unknown,
): value is { text?: unknown; appliesTo?: unknown } {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('text' in value || 'appliesTo' in value)
  );
}

/** Strict shape narrowing — guarantees `.text` is a string before use. */
export function isFreightInstructionTextBlock(
  value: unknown,
): value is { text: string } {
  return isFreightInstructionBlockLike(value) && typeof value.text === 'string';
}

/** True when the value is a list of freight-instruction blocks. */
export function isFreightInstructionBlockList(value: unknown): boolean {
  return Array.isArray(value) && value.some(isFreightInstructionBlockLike);
}

/**
 * Text a textarea (or read-only cell) should show for a freight-instruction
 * value: a string as-is, a block list joined by blank lines, anything else ''.
 */
export function freightInstructionText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (isFreightInstructionBlockList(value) && Array.isArray(value)) {
    return value
      .filter(isFreightInstructionTextBlock)
      .map((b) => b.text.trim())
      .filter((t) => t.length > 0)
      .join('\n\n');
  }
  return '';
}
